import { useState, useEffect, useRef } from 'react';
import { Flex, Box, Input, Textarea, Switch } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { LuX, LuUpload } from 'react-icons/lu';
import { LANGUAGE_OPTIONS } from '../../constants/currencies';
import {
  createCategory,
  updateCategory,
  translateFields,
  type CategoryResponse,
  type TranslatableField,
} from '../../api/menus';
import { toast } from 'react-toastify';
import ModalShell, { CancelButton, SaveButton, DeleteIconButton, FieldLabel, LanguageTabs, inputStyles } from './ModalShell';
import ImagePickerModal from '../ImagePickerModal';

interface CategoryModalProps {
  open: boolean;
  onClose: () => void;
  menuId: string;
  category: CategoryResponse | null;
  isCreating: boolean;
  availableLanguages: string[];
  defaultLanguage: string;
  pendingImageUrl?: string | null;
  onSaved: (category: CategoryResponse, isNew: boolean) => void;
  onDelete: (categoryId: string) => void;
  onImageReady: (blob: Blob) => Promise<void>;
  onRemoveImage: () => void;
}

const CategoryModal = ({
  open,
  onClose,
  menuId,
  category,
  isCreating,
  availableLanguages,
  defaultLanguage,
  pendingImageUrl,
  onSaved,
  onDelete,
  onImageReady,
  onRemoveImage,
}: CategoryModalProps) => {
  const { t } = useTranslation();
  const [names, setNames] = useState<TranslatableField>({});
  const [descriptions, setDescriptions] = useState<TranslatableField>({});
  const [visible, setVisible] = useState(true);
  const [editLang, setEditLang] = useState(defaultLanguage);
  const [isSaving, setIsSaving] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const prevOpen = useRef(false);

  useEffect(() => {
    if (open && !prevOpen.current) {
      if (isCreating) {
        setNames({});
        setDescriptions({});
        setVisible(true);
      } else if (category) {
        setNames({ ...category.name });
        setDescriptions(category.description ? { ...category.description } : {});
        setVisible(category.is_visible !== false);
      }
      setEditLang(defaultLanguage);
    }
    prevOpen.current = open;
  }, [open, category, isCreating, defaultLanguage]);

  const handleImageReady = async (blob: Blob) => {
    await onImageReady(blob);
    setShowImagePicker(false);
  };

  const handleAutoTranslate = async () => {
    const defaultName = names[defaultLanguage]?.trim();
    const defaultDesc = descriptions[defaultLanguage]?.trim();
    if (!defaultName && !defaultDesc) {
      toast.error(t('menus.fillDefaultLanguageFirst', 'Please fill in the default language fields first.'));
      return;
    }

    const fields: { [key: string]: string } = {};
    if (defaultName) fields.name = defaultName;
    if (defaultDesc) fields.description = defaultDesc;

    // Only translate to languages that have empty fields
    const targetLanguages = availableLanguages.filter(lang => {
      if (lang === defaultLanguage) return false;
      const nameEmpty = !names[lang]?.trim();
      const descEmpty = !descriptions[lang]?.trim();
      return nameEmpty || descEmpty;
    });

    if (targetLanguages.length === 0) {
      toast.info(t('menus.allFieldsFilled', 'All translation fields are already filled.'));
      return;
    }

    setIsTranslating(true);
    try {
      const result = await translateFields(menuId, { fields, target_languages: targetLanguages });
      setNames(prev => {
        const updated = { ...prev };
        for (const lang of targetLanguages) {
          if (!updated[lang]?.trim() && result.translations[lang]?.name) {
            updated[lang] = result.translations[lang].name;
          }
        }
        return updated;
      });
      setDescriptions(prev => {
        const updated = { ...prev };
        for (const lang of targetLanguages) {
          if (!updated[lang]?.trim() && result.translations[lang]?.description) {
            updated[lang] = result.translations[lang].description;
          }
        }
        return updated;
      });
      toast.success(t('menus.translationComplete', 'Translations filled. Review and save.'));
    } catch (error) {
      toast.error(t('menus.translationFailed', 'Auto-translation failed.'));
      console.error(error);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const cleanNames = Object.fromEntries(Object.entries(names).filter(([, v]) => v.trim()));
      const cleanDescs = Object.fromEntries(Object.entries(descriptions).filter(([, v]) => v.trim()));
      const descPayload = Object.keys(cleanDescs).length > 0 ? cleanDescs : undefined;

      if (isCreating) {
        const newCat = await createCategory(menuId, { name: cleanNames, description: descPayload, is_visible: visible });
        onSaved(newCat, true);
      } else if (category) {
        const updated = await updateCategory(menuId, category.id, { name: cleanNames, description: descPayload, is_visible: visible });
        onSaved(updated, false);
      }
      onClose();
    } catch (error) {
      toast.error(isCreating ? t('menus.createCategoryFailed') : t('menus.updateCategoryFailed'));
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={isCreating ? t('menus.addCategory') : t('menus.editCategory')}
      footer={
        <>
          {category && !isCreating && (
            <DeleteIconButton onClick={() => onDelete(category.id)} />
          )}
          <CancelButton onClick={onClose} label={t('common.cancel')} />
          <SaveButton onClick={handleSave} loading={isSaving} label={t('common.save')} />
        </>
      }
    >
      <Flex direction="column" rowGap="24px">
        <LanguageTabs
          languages={availableLanguages}
          activeLang={editLang}
          onSelect={setEditLang}
          options={LANGUAGE_OPTIONS}
          onAutoTranslate={availableLanguages.length > 1 ? handleAutoTranslate : undefined}
          isTranslating={isTranslating}
        />

        {/* Category Image */}
        <Flex direction="column" rowGap="8px">
          <FieldLabel>{t('menus.image')}</FieldLabel>
          {(category?.image_url || pendingImageUrl) ? (
              <Box position="relative" width="80px" height="80px">
                <Box
                  width="80px"
                  height="80px"
                  borderRadius="8px"
                  borderWidth="1px"
                  borderColor="neutral.700"
                  overflow="hidden"
                  cursor="pointer"
                  onClick={() => setShowImagePicker(true)}
                  _hover={{ borderColor: 'neutral.600' }}
                >
                  <img src={category?.image_url || pendingImageUrl || ''} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
                <button
                  type="button"
                  onClick={onRemoveImage}
                  style={{
                    position: 'absolute', top: -6, right: -6,
                    width: 20, height: 20, padding: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: '#111111', border: '1px solid #383838',
                    borderRadius: '50%', cursor: 'pointer', zIndex: 2,
                  }}
                >
                  <LuX size={10} color="#9CA4B1" />
                </button>
              </Box>
            ) : (
              <Flex
                alignItems="center"
                gap="12px"
                py="12px"
                px="16px"
                borderRadius="8px"
                borderWidth="1px"
                borderStyle="dashed"
                borderColor="neutral.600"
                backgroundColor="neutral.700"
                cursor="pointer"
                transition="all 0.2s"
                onClick={() => setShowImagePicker(true)}
                _hover={{ borderColor: 'neutral.500', backgroundColor: 'neutral.600' }}
              >
                <Box p="8px" borderRadius="8px" backgroundColor="neutral.600" flexShrink={0}>
                  <LuUpload size={18} color="#CFD5DE" />
                </Box>
                <Flex direction="column" gap="2px">
                  <Box fontSize="14px" fontWeight="400" color="neutral.300">{t('menus.uploadImage')}</Box>
                  <Box fontSize="12px" fontWeight="400" color="neutral.500">PNG, JPG, WebP</Box>
                </Flex>
              </Flex>
            )}
          <ImagePickerModal
            open={showImagePicker}
            onClose={() => setShowImagePicker(false)}
            onImageReady={handleImageReady}
          />
          </Flex>

        <Flex direction="column" rowGap="8px">
          <FieldLabel>{t('menus.categoryName')}</FieldLabel>
          <Input
            value={names[editLang] || ''}
            onChange={(e) => setNames(prev => ({ ...prev, [editLang]: e.target.value }))}
            placeholder={t('menus.categoryName')}
            {...inputStyles}
          />
        </Flex>

        <Flex direction="column" rowGap="8px">
          <FieldLabel>{t('menus.description')}</FieldLabel>
          <Textarea
            value={descriptions[editLang] || ''}
            onChange={(e) => setDescriptions(prev => ({ ...prev, [editLang]: e.target.value }))}
            placeholder={t('menus.enterDescription')}
            {...inputStyles}
            rows={3}
          />
        </Flex>

        {/* Toggles */}
        <Flex alignItems="center" gap="8px">
          <Switch.Root checked={visible} onCheckedChange={(e) => setVisible(e.checked)}>
            <Switch.HiddenInput />
            <Switch.Control><Switch.Thumb /></Switch.Control>
          </Switch.Root>
          <Box fontSize="14px" color="neutral.300">{t('menus.visible')}</Box>
        </Flex>
      </Flex>
    </ModalShell>
  );
};

export default CategoryModal;
